import BUDGET_CONSTS, { DEEP_CONVERSION_TYPE, OPT_GOAL, BID_SCENE, COST_CONTROL, SEARCH_EXPANSION_SWITCH, SEARCH_EXPAND_TARGETING_SWITCH, SMART_DELIVERY_GOAL } from '@/views/create/gdt/model/budget/budget-const.js'
import { makeBidAdjustmentParams } from '@/views/create/gdt/model/budget/bid-adjustment.js'
import { SITE_ENUM, WECHAT_SITES, EXPLORATION_ENUM, SEARCH_AD_SITE_ENUM } from '@/views/create/gdt/model/siteset/siteset-const.js'
import { BRAND_ENUM, PAGE_TYPES, DELIVERY_MODE, DYNAMIC_CREATIVE_TYPE } from '@/views/create/gdt/model/creative-const.js'
import { PROMOTED_TYPE, PLATFORM, IMG_URL_MD5_REGEXP, AD_MARKETING_SCENE, SMART_DELIVERY_PLATFORM } from '@/views/create/gdt/model/project-const.js'
import moment from 'moment'
import { omit, clone, pick } from '@/common/js/just'

function validateCarouselMaterials(materials, materialName) {
  if (materials.length !== 3) throw new Error(`轮播三${materialName}创意必须选择3个${materialName === '图' ? '图片' : '视频'}素材`)
  if (materials.some((item) => Number(item.width) <= 0 || Number(item.height) <= 0)) {
    throw new Error(`轮播三${materialName}创意的素材缺少有效的宽高尺寸`)
  }
  const materialSizes = new Set(materials.map((item) => `${Number(item.width)}*${Number(item.height)}`))
  if (materialSizes.size !== 1) throw new Error(`轮播三${materialName}创意的3个素材宽高尺寸必须一致`)
}

/**
 * @description: 构造计划表格行勾选状态
 * @return {*}
 */
export const getDefaultCheckStates = () => {
  return {
    accountCheck: false, // 广告账号勾选
    targetCheck: false, // 定向包勾选
    creativeCheck: false, // 创意组勾选
    dailyBudgetCheck: false, // 每日预算勾选
    totalBudgetCheck: false, // 总预算勾选
    bidCheck: false, // 出价勾选
    convertCheck: false, // 转化勾选
    populationCheck: false, // 人群包勾选
    pagesCheck: false, // 落地页
    pagesV3Check: false, // 3.0落地页
    profileCheck: false // 头像昵称及跳转页
  }
}

export const getDefaultModalStates = () => {
  return {
    showGlobalAccountModify: false,
    showGlobalTargetModify: false,
    showGlobalCreativeModify: false,
    showGlobalBudgetModify: false,
    showGlobalTotalBudgetModify: false,
    showGlobalBidModify: false,
    showGlobalConvertModify: false,
    showGlobalPopModify: false,
    showGlobalPagesModify: false,
    showGlobalPagesV3Modify: false,
    showGlobalProfileModify: false
  }
}

/**
 * @description: 构造内部计划参数
 * @return {*}
 */
export function makeInnerPlanParams(row) {
  const { project_name, ch_platform_id, game_id, callback_switch, monitorPlan } = row.plan
  const { id: gameId, ad_package } = game_id

  const params = {
    plan_name: project_name,
    ch_platform_id,
    game_id: gameId,
    callback_switch: +callback_switch,
    package: ad_package
  }

  if (monitorPlan?.length) {
    params.plan_auto_task_id = monitorPlan.map((p) => p.id).join(',')
  }
  return params
}

/**
 * @description: 腾讯广告3.0构造内部计划参数
 * @param {*} row
 */
export function makeV3InnerPlanParams(row) {
  return makeInnerPlanParams(row)
}

/**
 * @description: 腾讯广告3.0构造计划创意参数
 * @return {*}
 */
export function makeV3CreativeParams(row) {
  const {
    groupName, components, delivery_mode, description,
    dynamic_creative_name, creative_template_id,
    dynamic_creative_type, selectedAssets, pageInfo
  } = row.creative
  const { video, image, image_list } = selectedAssets
  const {
    mini_card_link,
    brand,
    action_button,
    end_page,
    app_gift_pack_code,
    barrage,
    floating_zone,
    label,
    show_data,
    social_skill,
    text_link,
    wechat_channels,
    wxgame_playable_page,
    wxgame_playable_material_config,
    wxgame_playable_component
  } = components

  if (!pageInfo?.length) throw new Error('请选择落地页')
  const jump_info = pageInfo?.map(({ page, pageType }) => {
    const isIOS = PLATFORM.IOS === row.plan.ch_platform_id
    const isAndroid = PLATFORM.ANDROID === row.plan.ch_platform_id
    const isMiniGame = PLATFORM.MINIGAME === row.plan.ch_platform_id

    let pageItem = {
      page_type: PAGE_TYPES.XJ_QUICK,
      page_spec: {
        xj_quick_spec: {
          page_id: page?.page_id
        }
      }
    }
    // pageType的定义： src/views/create/gdt/components/distribute/components/ad-langingpage-v3.vue
    if (pageType === 5) {
      pageItem = {
        page_type: PAGE_TYPES.OFFICIAL,
        page_spec: {
          official_spec: {
            page_id: page?.page_id,
            page_url: page.page_url
          }
        }
      }
    }
    if (isIOS && pageType === 3) {
      pageItem = {
        page_type: PAGE_TYPES.IOS_APP
      }
    }

    if (isMiniGame && !page.page_id) {
      pageItem = {
        page_type: PAGE_TYPES.WECHAT_MINI_GAME
      }
    }
    return {
      value: pageItem
    }
  })

  // 后端接口需要标记的参数
  const materials = []

  const creativeParams = {
    delivery_mode,
    dynamic_creative_type,
    ideaName: groupName,
    dynamic_creative_name
  }

  let isWxGamePlayable = false
  let isCarouselThreeImage = false
  let isCarouselThreeVideo = false

  // 自定义创意-手动匹配，添加创意形式
  // 20260126新增组件化创意-手动匹配，添加创意形式
  if (delivery_mode === DELIVERY_MODE.CUSTOMIZE || delivery_mode === DELIVERY_MODE.COMPONENT) {
    if (dynamic_creative_type === DYNAMIC_CREATIVE_TYPE.COMMON) {
      creativeParams.creative_template_id = creative_template_id
      // 手动匹配只有激励浏览计划需要按小游戏试玩处理
      isWxGamePlayable = creative_template_id === 910
      isCarouselThreeImage = creative_template_id === 2688
      isCarouselThreeVideo = creative_template_id === 2888
      if (creative_template_id === 910) {
        // if (brand.brandType !== BRAND_ENUM.BRAND) throw new Error('品牌形象跳转在激励浏览广告创意形式时只能选择【品牌跳转】')
      }
    }
  }

  const creative_components = {}

  const validDescList = description.filter(desc => !!desc.content)
  if (validDescList.length) {
    creative_components.description = validDescList.map((desc) => ({ value: desc }))
  }

  if (isCarouselThreeVideo) validateCarouselMaterials(video || [], '视频')
  if (isCarouselThreeImage) validateCarouselMaterials(image || [], '图')
  if (isCarouselThreeVideo && (image?.length || image_list?.length)) throw new Error('轮播三视频创意只能提交视频素材')
  if (isCarouselThreeImage && (video?.length || image_list?.length)) throw new Error('轮播三图创意只能提交图片素材')

  if (video?.length) {
    const videoComponents = video.map((v) => {
      // external 是否外部媒体素材标记，在src/components/material-selector/utils.js中定义
      const videoObj = {
        key: `@${v.id}`,
        type: 'VIDEO',
        inner_id: `${v.id}`
      }
      materials.push(videoObj)

      const coverObj = {
        key: `@${v.cover_id}`,
        type: 'IMAGE',
        inner_id: `${v.cover_id}`,
        // defaultCover为false时，代表封面被替换过且替换成了图片封面
        defaultCover: !v.cover_replaced_type || v.cover_replaced_type === 'poster'
      }

      // 是外部媒体参数且没有更换封面，使用原本的outer_id
      if (v.external && !v.cover_replaced) {
        delete coverObj.inner_id
        coverObj.outer_id = `${v.cover_id}`
      }
      // 新增视频图片封面传参
      materials.push(coverObj)

      return {
        value: {
          video_id: `@${v.id}`,
          cover_id: `@${v.cover_id}`
        }
      }
    })

    if (isCarouselThreeVideo) {
      creative_components.video_list = [
        {
          value: {
            list: videoComponents.map((item) => item.value)
          }
        }
      ]
    } else {
      creative_components.video = videoComponents
    }
  }

  if (image?.length) {
    const imageComponents = image.map((i) => {
      // external 是否外部媒体素材标记，在src/components/material-selector/utils.js中定义
      materials.push({
        key: `@${i.id}`,
        type: 'IMAGE',
        inner_id: `${i.id}`,
        // 比例
        ratio: i.width / i.height
      })
      return { value: { image_id: `@${i.id}` } }
    })

    if (isCarouselThreeImage) {
      creative_components.image_list = [
        {
          value: {
            list: imageComponents.map((item) => item.value),
            display_mode: 'CAROUSEL'
          }
        }
      ]
    } else {
      creative_components.image = imageComponents
    }
  }

  if (image_list?.length && !isCarouselThreeImage) {
    creative_components.image_list = image_list.map((i, index) => {
      // i.external 是否外部媒体素材标记，在src/components/material-selector/utils.js中定义
      const internalImageList = []
      const externalImageList = []
      const imageObj = i.image_list_urls.map((url) => {
        const [md5] = url.match(IMG_URL_MD5_REGEXP) || []
        internalImageList.push({
          image_id: `@${md5}`
        })
        return {
          key: `@${md5}`,
          type: 'GROUP_IMAGE',
          inner_id: `${i.id}`
        }
      })
      materials.push(...imageObj)
      return { value: { list: [...internalImageList, ...externalImageList] } }
    })
  }

  const { profileInfo, brandType, brandInfo, officialAccountInfo } = brand
  const { channelInfo, finder_object_visibility } = wechat_channels
  // 视频号
  if (brandType === BRAND_ENUM.CHANNEL) {
    const { promoted_object_id } = channelInfo
    if (!promoted_object_id) throw new Error('请选择品牌形象跳转')
    creative_components.wechat_channels = [
      {
        value: {
          username: promoted_object_id,
          finder_object_visibility
        }
      }
    ]

    creative_components.brand = [
      {
        value: {
          jump_info: {
            page_type: 'PAGE_TYPE_WECHAT_CHANNELS_PROFILE',
            page_spec: {
              wechat_channels_profile_spec: {
                username: promoted_object_id
              }
            }
          }
        }
      }
    ]
  }
  // 公众号
  if (brandType === BRAND_ENUM.OFFICIAL_ACCOUNT) {
    const { promoted_object_id } = officialAccountInfo
    if (!promoted_object_id) throw new Error('请选择品牌形象跳转')
    creative_components.brand = [
      {
        value: {
          jump_info: {
            page_type: 'PAGE_TYPE_WECHAT_OFFICIAL_ACCOUNT_DETAIL',
            page_spec: {
              wechat_official_account_detail_spec: {
                app_id: promoted_object_id
              }
            }
          }
        }
      }
    ]
  }

  // 品牌简介页
  if (brandType === BRAND_ENUM.PROFILE) {
    const { profile_name, head_image_id, profile_id } = profileInfo
    if (!profile_name) throw new Error('请选择品牌形象跳转')
    creative_components.brand = [
      {
        value: {
          brand_name: profile_name,
          brand_image_id: head_image_id || undefined,
          jump_info: {
            page_type: 'PAGE_TYPE_H5_PROFILE',
            page_spec: {
              h5_profile_spec: {
                page_id: profile_id
              }
            }
          }
        }
      }
    ]
  }

  // 品牌形象
  if (brandType === BRAND_ENUM.BRAND) {
    const { name, image_id } = brandInfo
    if (!name) throw new Error('请选择品牌形象跳转')

    creative_components.brand = [
      {
        value: {
          brand_name: name,
          brand_image_id: image_id || undefined
        }
      }
    ]
  }

  if (end_page?.switch) {
    // 结束页
    creative_components.end_page = [
      {
        value: {
          end_page_type: end_page.end_page_type,
          end_page_desc: end_page.end_page_desc
        }
      }
    ]
  }

  // 礼包码
  if (app_gift_pack_code?.switch) {
    creative_components.app_gift_pack_code = [
      {
        value: {
          code: app_gift_pack_code.code,
          description: app_gift_pack_code.description
        }
      }
    ]
  }

  // 弹幕
  if (barrage?.length) {
    creative_components.barrage = [
      {
        value: {
          list: barrage.map((item) => ({ id: item.id, text: item.text }))
        }
      }
    ]
  }

  // 标签
  if (label?.length) {
    creative_components.label = [
      {
        value: {
          list: label.map((item) => ({ content: item.name }))
        }
      }
    ]
  }

  // 数据外显
  if (show_data?.switch) {
    const { conversion_data_type, conversion_target_type } = show_data
    const showDataValue = {
      conversion_data_type,
      conversion_target_type: 'CONVERSION_TARGET_DEFAULT'
    }

    if (conversion_data_type === 'CONVERSION_DATA_ADMETRIC') {
      showDataValue.conversion_target_type = conversion_target_type
    }
    creative_components.show_data = [
      {
        value: showDataValue
      }
    ]
  }

  // 版位里面包含了“朋友圈”时才能够使用；对于多个定向版位有的有有的没有朋友圈，
  // 如果进行了首条评论的设置，只在有朋友圈的广告里生效，没有朋友圈的广告则不进行提交
  if (social_skill && row.plan.siteSet.includes(SITE_ENUM.MOMENTS)) {
    const { social_skill_first_comment_switch } = social_skill
    if (social_skill_first_comment_switch) {
      creative_components.social_skill = [
        {
          value: social_skill
        }
      ]
    }
  }

  // 主落地页
  creative_components.main_jump_info = jump_info

  // 行动按钮
  if (action_button?.switch) {
    creative_components.action_button = [
      {
        value: {
          button_text: action_button.value
        }
      }
    ]
  }

  // 浮层卡片
  if (floating_zone?.floating_zone_switch) {
    const { floating_zone_image, external, ...restParams } = floating_zone
    const floating_zone_image_id = floating_zone_image.id
    materials.push({
      key: `@${floating_zone_image_id}`,
      type: 'LOGO',
      inner_id: floating_zone_image_id
    })
    creative_components.floating_zone = [
      {
        value: {
          ...restParams,
          floating_zone_image_id: `@${floating_zone_image_id}`
          // jump_info: jump_info[0].value
        }
      }
    ]
  }

  // 文字链
  if (text_link?.switch) {
    creative_components.text_link = jump_info.map((page) => {
      return {
        value: {
          link_name_type: text_link.value,
          jump_info: page.value
        }
      }
    })
  }

  // 图文链接
  if (mini_card_link?.switch) {
    const { mini_card_link_image, mini_card_link_description, mini_card_link_button_text, page_type, external } = mini_card_link
    const mini_card_link_image_id = mini_card_link_image?.id
    if (mini_card_link_image_id) {
      materials.push({
        key: `@${mini_card_link_image_id}`,
        type: 'LOGO',
        inner_id: mini_card_link_image_id
      })
    }

    const miniCardLinkJumpInfo = {
      page_type
    }

    // 当图文链接配置跳转落地页为官方落地页时，找到配置的官方落地页作为跳转落地页
    if (page_type === PAGE_TYPES.OFFICIAL) {
      const page = jump_info.find(i => i.value.page_type === page_type)
      const { page_spec } = page?.value || {}
      if (page_spec) {
        miniCardLinkJumpInfo.page_spec = page_spec
      }
    }
    creative_components.mini_card_link = [
      {
        value: {
          mini_card_link_description: mini_card_link_description,
          mini_card_link_button_text: mini_card_link_button_text,
          mini_card_link_image: mini_card_link_image_id ? `@${mini_card_link_image_id}` : undefined,
          jump_info: miniCardLinkJumpInfo
        }
      }
    ]
  }

  // 试玩营销组件，当自动版位或者包含朋友圈版位时可以提交
  if (row.plan.siteSet.includes(SITE_ENUM.MOMENTS) || row.plan.automatic_site_enabled) {
    const { switch: wxgame_playable_component_switch } = wxgame_playable_component || {}
    if (wxgame_playable_component_switch) {
      const {
        wxgame_playable_page_path, card_link_image, end_cover_img, wxgame_playable_page_trigger_text,
        wxgame_playable_page_trigger_types, wxgame_playable_page_end_desc, wxgame_playable_page_card_link_description,
        wxgame_playable_page_end_time_type
      } = wxgame_playable_component

      if (!card_link_image.id || !end_cover_img.id) throw new Error('试玩营销组件图片参数错误')

      materials.push({
        key: `@${card_link_image.id}`,
        type: 'IMAGE',
        inner_id: card_link_image.id
      })

      materials.push({
        key: `@${end_cover_img.id}`,
        type: 'IMAGE',
        inner_id: end_cover_img.id
      })

      creative_components.wxgame_playable_page = [
        {
          value: {
            wxgame_playable_page_switch: wxgame_playable_component_switch,
            wxgame_playable_page_path,
            wxgame_playable_page_end_cover_img: `@${end_cover_img.id}`,
            wxgame_playable_page_end_desc,
            wxgame_playable_page_trigger_types,
            wxgame_playable_page_trigger_text,
            wxgame_playable_page_card_link_image: `@${card_link_image.id}`,
            wxgame_playable_page_card_link_description,
            wxgame_playable_page_end_time_type
          }
        }
      ]
    }
  }
  // 小程序试玩页
  if (isWxGamePlayable) {
    // 去除创意组件内的图片，试玩素材不需要提交
    creative_components.image = undefined
    // 去除创意组件的文案，试玩素材不需要提交
    creative_components.description = undefined

    let cover_id = null
    let page_end_desc = ''
    let playable_page_path = ''
    let wxgame_playable_page_end_time_type

    // 批量新建流程
    if (wxgame_playable_material_config) {
      const { playable_item, wxgame_playable_page_end_time_type: endTimeType } = wxgame_playable_material_config
      const { playable_page_path: path } = playable_item
      if (!path) throw new Error('请选择试玩页')
      cover_id = image[0]?.id
      page_end_desc = description[0].content
      playable_page_path = path
      wxgame_playable_page_end_time_type = endTimeType
    }

    // 批量失败修改，批量复制流程
    if (!wxgame_playable_material_config && wxgame_playable_page) {
      const {
        wxgame_playable_page_end_cover_img,
        wxgame_playable_page_end_desc,
        wxgame_playable_page_path,
        wxgame_playable_page_end_time_type: endTimeType
      } = wxgame_playable_page || {}
      if (!wxgame_playable_page_path) throw new Error('请选择试玩页')
      cover_id = wxgame_playable_page_end_cover_img?.id
      page_end_desc = wxgame_playable_page_end_desc
      playable_page_path = wxgame_playable_page_path
      wxgame_playable_page_end_time_type = endTimeType
    }

    if (cover_id) {
      materials.push({
        key: `@${cover_id}`,
        type: 'LOGO',
        inner_id: cover_id
      })
    }
    creative_components.wxgame_playable_page = [
      {
        value: {
          wxgame_playable_page_switch: true,
          wxgame_playable_page_end_cover_img: `@${cover_id}`,
          wxgame_playable_page_end_desc: page_end_desc,
          wxgame_playable_page_path: playable_page_path,
          wxgame_playable_page_end_time_type
        }
      }
    ]
    creativeParams.materials = materials
    const { action_button, brand, main_jump_info, show_data, wxgame_playable_page: page } = creative_components
    // 试玩素材指定参数提交
    creativeParams.creative_components = {
      action_button, brand, main_jump_info, show_data, wxgame_playable_page: page
    }

    return creativeParams
  }

  creativeParams.creative_components = creative_components
  creativeParams.materials = materials
  return creativeParams
}

/**
 * @description: 构造计划创意参数
 * @return {*}
 */
export function makeCreativeParams(row) {
  const { groupName, idea_type, adcreative_template_id, creativeFormModel, pageInfo, link_name_type, profile } = row.creative
  const {
    brand,
    button_text,
    description_options,
    video_options,
    image_options,
    title_options,
    element_story,
    label,
    finder_object_visibility,
    wechat_channels_spec,
    floating_zone_struct,
    end_page
  } = creativeFormModel
  const creativeLabel = label?.content ? label.content.split(',') : []

  // 创意组件配置信息
  const creativeElements = {
    button_text,
    finder_object_visibility
  }
  // 文案
  if (description_options) {
    creativeElements.description_options = description_options.map((desc) => desc.text)
  }

  // 广告文案
  if (title_options) {
    creativeElements.title_options = title_options.map((t) => t.text)
  }

  // 视频
  const imageCovers = {}
  if (video_options) {
    video_options
      .filter((video) => video.imageCover)
      .forEach((video) => {
        imageCovers[video.cover_id] = true
      })

    creativeElements.video_options = video_options.map((video) => video.id)

    // 视频封面
    creativeElements.image_options = video_options?.map((video) => video.cover_id)
  }
  if (image_options) {
    // 图片
    creativeElements.image_options = image_options?.map((image) => image.id)
  }
  // 品牌名称
  if (brand) {
    creativeElements.brand = {
      brand_img: brand.id,
      brand_name: brand.brand_name
    }
  }

  // 三小图
  if (element_story && element_story?.image?.length) {
    const elementStory = element_story.image.map((i) => ({ image: i.id }))
    creativeElements.element_story = elementStory
  }

  if (floating_zone_struct) {
    // 浮层卡片
    const { floating_zone_switch, type, name, desc, floatingZoneImage, button_text } = floating_zone_struct

    const switchOpenedParams = {
      floating_zone_name: name,
      floating_zone_switch,
      floating_zone_desc: desc,
      floating_zone_type: type,
      floating_zone_image_id: floatingZoneImage?.id || '',
      floating_zone_button_text: button_text
    }

    const switchClosedParams = {
      floating_zone_switch: false
    }

    creativeElements.floating_zone_struct = floating_zone_switch ? switchOpenedParams : switchClosedParams
  }

  // 微信视频号信息
  if (wechat_channels_spec) {
    creativeElements.wechat_channels_spec = wechat_channels_spec
  }

  // 标签内容
  if (creativeLabel?.length) {
    creativeElements.label = creativeLabel.map((label) => ({ content: label }))
  }

  // 结束页文案、视频结束页类型
  if (end_page) {
    creativeElements.end_page = end_page
  }

  // 计划创意
  let creativeParams = {
    ideaName: groupName,
    adcreative_name: row.plan.project_name,
    idea_type,
    adcreative_template_parent: adcreative_template_id,
    adcreative_template_id,
    deep_link_url: '',
    promoted_object_type: row.plan.promoted_object_type,
    campaign_type: 'CAMPAIGN_TYPE_NORMAL', // ['CAMPAIGN_TYPE_SEARCH' | 'CAMPAIGN_TYPE_NORMAL']
    adcreative_elements_array: [
      {
        dynamic_creative_group_used: 'VIDEO_IMAGE_GROUP',
        adcreative_template_id,
        dynamic_creative_elements: creativeElements,
        image_covers: imageCovers
      }
    ]
  }

  // 安卓推广目的，单视频号，没有选落地页报错，这个报错有疑问？明明是没有落地页，却提示暂不支持含有引用外层素材组件的落地页
  // 失败, step3 - 2新建项目创意失败, 操作失败, 腾讯广告API, 创建动态创意失败,
  // The currently selected landing page cannot be used in the current delivery scene, 暂不支持含有引用外层素材组件的落地页 trace_id: 9af09dc989030fe8742f54bbae65a78a

  // 落地页配置信息
  const { page_spec, pageType } = pageInfo || {}
  const landingPages = {}
  if (page_spec?.page_id) {
    landingPages.wechat_page = pageType
    landingPages.page_spec = {
      page_id: page_spec.page_id
      // page_url: page_spec.preview_url
    }
  }
  if (pageInfo) {
    creativeParams = Object.assign({}, creativeParams, landingPages)
  }

  const { siteSet, promoted_object_type } = row.plan
  if (siteSet.includes(SITE_ENUM.MOMENTS)) {
    // 朋友圈版位添加链接名称类型
    creativeParams = Object.assign({}, creativeParams, { link_name_type })

    // 朋友圈版位，添加文字链跳转类型
    let link_page_type = ''

    switch (promoted_object_type) {
      case PROMOTED_TYPE.APP_ANDROID:
        link_page_type = ['LINK_PAGE_TYPE_NOT_USED', 'LINK_PAGE_TYPE_CANVAS_WECHAT', 'LINK_PAGE_TYPE_XJ_QUICK'][pageType]

        // 安卓推广目的，视频号通投
        if (siteSet.length > 1 && siteSet.includes(SITE_ENUM.CHANNELS)) {
          link_page_type = 'LINK_PAGE_TYPE_XJ_ANDROID_APP_H5'
        }
        break

      case PROMOTED_TYPE.APP_IOS:
        if (siteSet.includes(SITE_ENUM.MOMENTS)) {
          link_page_type = ['LINK_PAGE_TYPE_NOT_USED', 'LINK_PAGE_TYPE_CANVAS_WECHAT', 'LINK_PAGE_TYPE_XJ_QUICK'][pageType]
        } else {
          link_page_type = 'LINK_PAGE_TYPE_DEFAULT'
        }
        break

      // 小程序推广目的，朋友圈版位
      case PROMOTED_TYPE.MINI_GAME_WECHAT:
        link_page_type = 'LINK_PAGE_TYPE_MINI_GAME_WECHAT'
        break

      default:
        break
    }
    creativeParams = Object.assign({}, creativeParams, { link_page_type })
  }

  // 头像昵称及跳转页（仅微信朋友圈广告（site_set=SITE_SET_MOMENTS）的创意形式支持)
  if (siteSet.includes(SITE_ENUM.MOMENTS)) {
    const { profile_id } = profile || {}
    if (profile_id) {
      creativeParams = Object.assign({}, creativeParams, { profile_id })
    }
  }

  // 视频号通投时添加参数，通常创意形式为横板、竖版视频username会有值
  const username = wechat_channels_spec?.username
  if (siteSet.includes(SITE_ENUM.CHANNELS) && siteSet.length > 1 && username) {
    const headClickType = {
      head_click_type: 'HEAD_CLICK_TYPE_LIVE_PROFILE'
    }
    // 视频号通投包含朋友圈时加上head_click_spec
    // 根据报错：您指定的adcreative_template_id，site_set和page_type不支持参数head_click_spec 推测出来的
    // 媒体暂未出具体文档 只能靠报错推测修改 2023.4.20
    if (siteSet.includes(SITE_ENUM.MOMENTS)) {
      headClickType.head_click_spec = {
        finder_username: username
      }
    }
    creativeParams = Object.assign({}, creativeParams, headClickType)
  }

  // 小程序推广目的，添加创意参数
  if (promoted_object_type === PROMOTED_TYPE.MINI_GAME_WECHAT) {
    creativeParams.wechat_game = 'PAGE_TYPE_MINI_GAME_WECHAT'
  }

  return creativeParams
}

const defaultTimeSeries = new Array(7 * 48).fill(1).join('')
/**
 * @description: 构造外部计划参数
 * @return {*}
 */
export function makeOuterPlanParams(row) {
  const planParams = makePlanParams(row)
  const bidParams = makeADABidParams(row)
  const targetParams = makeTargetParams(row)

  return {
    ...planParams,
    ...bidParams,
    targeting: targetParams
  }
}

/**
 * @description: 腾讯广告3.0构造外部计划参数
 * @return {*}
 */
export function makeV3OuterPlanParams(row, regionList) {
  const planParams = makeV3PlanParams(row)
  const bidParams = makeV3BidParams(row)
  const targetParams = makeV3TargetParams(row, regionList)

  return {
    ...planParams,
    ...bidParams,
    targeting: targetParams
  }
}

/**
 * @description: 构造定向参数
 * @param {Object} row
 * @return {Object}
 */
function makeTargetParams(row) {
  const {
    age,
    ageRange,
    audienceType,
    populations,
    deviceType,
    device,
    excludeDevice,
    devicePrice,
    excludeCustom,
    excluded_converted_audience,
    game_consumption_level,
    gender,
    region,
    regionType,
    locationTypes,
    network,
    osType,
    version,
    name,
    wechatAdBehaviorType,
    wechatAdBehavior
  } = row.target
  const { ad_marketing_scene } = row.plan
  const targetParams = {
    targetingName: name
  }

  // 自定义年龄段
  if (age === 1) {
    const [min, max] = ageRange.map(Number)
    targetParams.age = [{ min, max }]
  } else {
    // 单个年龄段
    if (age !== 0) {
      const [min, max] = age.split(',').map(Number)
      targetParams.age = [{ min, max }]
    }
  }
  // 设备品牌
  if (deviceType && device?.length) {
    const brandModel = {}
    if (excludeDevice) {
      brandModel.excluded_list = device
    } else {
      brandModel.included_list = device
    }
    targetParams.device_brand_model = brandModel
  }

  // 设备价格
  const [price] = devicePrice
  if (price) {
    targetParams.device_price = devicePrice
  }

  // 人群包
  if (audienceType) {
    // 批量失败修改使用row.plan.accounts[0]
    const accountId = row.plan.account?.id || row.plan.accounts[0]
    const audiences = populations[accountId]
    if (audiences) {
      const { includes, excludes } = audiences || {}
      if (includes?.length) {
        targetParams.custom_audience = includes
      }
      if (excludes?.length) {
        targetParams.excluded_custom_audience = excludes
      }
    }
  }

  // 排除已转化用户
  const { excluded_dimension, conversion_behavior_list, excluded_day } = excluded_converted_audience
  if (excluded_dimension) {
    const excludedAudience = { excluded_dimension }

    // 是否开启自定义转化行为
    if (excludeCustom) {
      excludedAudience.conversion_behavior_list = conversion_behavior_list
      excludedAudience.excluded_day = excluded_day
    }
    targetParams.excluded_converted_audience = excludedAudience
  }

  // 游戏消费能力
  const [level] = game_consumption_level
  if (level !== 0) {
    targetParams.game_consumption_level = game_consumption_level
  }
  // 性别
  if (gender) {
    targetParams.gender = [gender]
  }

  // 地域
  if (regionType) {
    targetParams.geo_location = {
      location_types: locationTypes,
      regions: region
    }
  }

  // 网络
  const [networkValue] = network
  if (networkValue) {
    targetParams.network_type = network
  }
  // 操作系统
  if (osType) {
    targetParams.user_os = version
  }

  // 微信再营销
  const [behaviorType] = wechatAdBehaviorType
  if (behaviorType) {
    targetParams.wechat_ad_behavior = wechatAdBehavior
  }

  // 智能投放定向参数
  if (ad_marketing_scene === AD_MARKETING_SCENE.VERSION_SMART) {
    const smartPromotionTargetKeys = ['age', 'gender', 'user_os', 'excluded_converted_audience', 'wechat_ad_behavior']
    return pick(targetParams, smartPromotionTargetKeys)
  }

  // 搜索广告定向参数
  if (ad_marketing_scene === AD_MARKETING_SCENE.VERSION_SEARCH) {
    const smartPromotionTargetKeys = ['age', 'gender', 'user_os', 'excluded_converted_audience', 'wechat_ad_behavior']
    return pick(targetParams, smartPromotionTargetKeys)
  }

  return targetParams
}

/**
 * @description: 腾讯广告3.0构造定向计划参数
 */
export function makeV3TargetParams(row, regionList) {
  // 是否微信流量
  const isWechatStream = WECHAT_SITES.some((s) => row.plan.siteSet.includes(s))
  const params = makeTargetParams(row)

  // 自动版位或微信流量，去掉港澳台
  if (isWechatStream || row.plan.automatic_site_enabled) {
    const { regions } = params.geo_location || {}

    const REGION = {
      ALL: 1156,
      TW: 710000,
      MAC: 820000,
      HK: 810000
    }

    if (regions?.length && regionList.length) {
      if (regions.includes(REGION.ALL)) {
        // 如果选的是全国，取除港澳台外的所有
        const [china] = regionList
        params.geo_location.regions = china.children.map((c) => c.id).filter((c) => ![REGION.TW, REGION.MAC, REGION.HK].includes(c))
      } else {
        // 如果选的是省份，去掉港澳台
        params.geo_location.regions = regions.filter((code) => ![REGION.TW, REGION.MAC, REGION.HK].includes(code))
      }
    }
  }

  return params
}

/**
 * Generates the function comment for the given function body.
 *
 * @param {Object} row - The input row object.
 * @return {Object} The generated plan parameters.
 */
function makePlanParams(row) {
  const {
    ad_marketing_scene,
    automatic_site_enabled,
    promotedObject,
    siteSet,
    exploration_strategy,
    priority_site_set,
    speed_mode,
    wechat_position,
    wechat_scene,
    scene_spec,
    flow_optimization_enabled,
    includeUnionType,
    excludeUnionType,
    displaySceneType,
    marketing_scene,
    pc_scene,
    pcScene,

    wechat_search_scene,
    wechatSearchScene,
    qbsearch_scene,
    qbSearchScene
  } = row.plan

  const { promoted_object_type, promoted_object_id } = promotedObject

  // 场景定向
  const scene_spec_params = {}

  // 只有包含优量汇版位时，才添加优量汇广告展示场景等参数
  if (siteSet.includes(SITE_ENUM.MOBILE_UNION)) {
    const { display_scene, exclude_union_position_package, include_union_position_package } = scene_spec
    if (displaySceneType && display_scene?.length) {
      scene_spec_params.display_scene = display_scene
    }

    // 批量失败修改使用row.plan.accounts[0]
    const accountId = row.plan.account?.id || row.plan.accounts[0]

    // 根据计划账号获取对应的屏蔽联盟流量包列表
    const excludePackage = exclude_union_position_package[accountId]
    if (excludeUnionType && excludePackage) {
      scene_spec_params.exclude_union_position_package = excludePackage
    }

    // 根据计划账号获取对应的定投联盟流量包列表
    const includePackage = include_union_position_package && include_union_position_package[accountId]
    if (includeUnionType && includePackage) {
      scene_spec_params.union_position_package = includePackage
    }
  }

  // 只有包含公众号版位时，才添加微信场景定投等参数
  // 20240319 增加自动版位的微信场景定投
  if (siteSet.includes(SITE_ENUM.WECHAT) || automatic_site_enabled) {
    // 设置对应的微信定投参数
    if (wechat_position) {
      const { wechatPostion } = row.plan
      scene_spec_params.wechat_position = wechatPostion
    }
    if (wechat_scene) {
      const { payScene, officialAccountMediaCate, miniGame } = row.plan
      scene_spec_params.wechat_scene = {
        official_account_media_category: officialAccountMediaCate,
        mini_program_and_mini_game: miniGame,
        pay_scene: payScene
      }
    }
  }

  if (siteSet.includes(SITE_ENUM.PCQQ)) {
    if (pc_scene) {
      scene_spec_params.pc_scene = pcScene
    }
  }

  if (ad_marketing_scene === AD_MARKETING_SCENE.VERSION_SEARCH) {
    if (siteSet.includes(SEARCH_AD_SITE_ENUM.QBSEARCH)) {
      if (qbsearch_scene) {
        scene_spec_params.qbsearch_scene = qbSearchScene
      }
    }
    if (siteSet.includes(SEARCH_AD_SITE_ENUM.WECHAT_SEARCH)) {
      if (wechat_search_scene) {
        scene_spec_params.wechat_search_scene = wechatSearchScene
      }
    }
  }

  const params = {
    automatic_site_enabled,
    promoted_object_type,
    promoted_object_id,
    speed_mode,
    marketing_scene,
    flow_optimization_enabled,
    scene_spec: scene_spec_params
  }
  if (!automatic_site_enabled) {
    params.site_set = siteSet
  } else {
    // 自动版位探索策略
    params.exploration_strategy = exploration_strategy
    // 优先版位集合，exploration_strategy = STEADY_EXPLORATION 时需要设置
    if (exploration_strategy === EXPLORATION_ENUM.STEADY) {
      params.priority_site_set = priority_site_set
    }
  }
  return params
}

/**
 * @description: 腾讯广告3.0外部计划参数
 * @param {*} row
 * @return {*}
 */
function makeV3PlanParams(row) {
  const { siteSet, promotedObject, marketing_goal, marketing_sub_goal, marketing_carrier_type, automatic_site_enabled, smart_targeting_mode } = row.plan
  const { promoted_object_id: marketing_carrier_id } = promotedObject
  const marketing_target_type = marketing_carrier_type.replace(/CARRIER_TYPE/, 'TARGET_TYPE')

  const marketingGoal = {
    marketing_goal,
    marketing_sub_goal,
    marketing_carrier_type,
    marketing_carrier_detail: {
      marketing_carrier_id
    },
    marketing_asset_outer_spec: {
      marketing_asset_outer_id: marketing_carrier_id,
      marketing_target_type
    }
  }

  // 与ada相同的参数，且去除speed_mode、marketing_scene、promoted_object_type、promoted_object_id
  const { speed_mode, marketing_scene, promoted_object_type, promoted_object_id, ...restPlanParams } = makePlanParams(row)
  // 只有包含视频号版位时，才添加微信视频号场景定投等参数
  if (siteSet.includes(SITE_ENUM.CHANNELS) || automatic_site_enabled) {
    const { wechatChannels, wechat_channels } = row.plan
    if (wechat_channels && wechatChannels?.length) {
      restPlanParams.scene_spec.wechat_channels_scene = wechatChannels
    }
  }

  // 定向模式：手动定向时传递 SMART_TARGETING_MANUAL
  if (smart_targeting_mode) {
    restPlanParams.smart_targeting_mode = 'SMART_TARGETING_MANUAL'
  }

  return {
    ...restPlanParams,
    ...marketingGoal
  }
}

/**
 * @description: 构造计划出价参数
 * @param {*} row
 * @return {*}
 */
function makeBidParams(row) {
  const {
    dateRange,
    timeRange,
    rangeDate,
    bid_mode,
    total_budget,
    daily_budget,
    deep_conversion_spec,
    deep_bid_mode,
    budget,
    bid_num_range,
    bid_amount,
    optimization_goal,
    time_series,
    access_type,
    bid_range,
    search_expansion_switch,
    search_expand_targeting_switch

  } = row.bid
  // 投放日期范围
  const [begin_date, end_date] = rangeDate

  const bidParams = {
    // ...restBidParams,
    bid_amount,
    bid_num_range,
    bid_mode,
    optimization_goal,
    total_budget,
    daily_budget: Number(daily_budget),
    // 默认今天
    begin_date: moment().format('YYYY-MM-DD'),
    // 如果希望长期投放，传空字符串
    end_date: '',
    access_type,
    bid_range
  }
  // 如果不希望长期投放，传入具体日期，大于等于今天，且大于等于 begin_date
  if (dateRange === 2) {
    bidParams.begin_date = begin_date
    bidParams.end_date = end_date
  }

  // 默认全天投放
  if (!timeRange) {
    bidParams.time_series = defaultTimeSeries
  } else {
    bidParams.time_series = time_series
  }

  // 日预算不限
  if (!budget) {
    bidParams.daily_budget = 0
  }

  // 深度转化参数
  if (deep_bid_mode) {
    const { deep_conversion_type, deep_conversion_behavior_spec, deep_conversion_worth_spec } = deep_conversion_spec
    const newDeepSpec = {}
    if (deep_conversion_type === DEEP_CONVERSION_TYPE.BEHAVIOR) {
      newDeepSpec.deep_conversion_behavior_spec = deep_conversion_behavior_spec
    }
    if (deep_conversion_type === DEEP_CONVERSION_TYPE.WORTH) {
      if (deep_conversion_worth_spec) {
        newDeepSpec.deep_conversion_worth_spec = {
          ...deep_conversion_worth_spec,
          expected_roi: Number(deep_conversion_worth_spec.expected_roi)
        }
      }
    }
    bidParams.deep_conversion_spec = { deep_conversion_type, ...newDeepSpec }
  }

  if (search_expansion_switch === SEARCH_EXPANSION_SWITCH.OPEN) {
    bidParams.search_expansion_switch = search_expansion_switch
    bidParams.search_expand_targeting_switch = search_expand_targeting_switch
  } else {
    // 单条修改需要改为close才能关闭
    bidParams.search_expansion_switch = SEARCH_EXPANSION_SWITCH.CLOSE
    bidParams.search_expand_targeting_switch = SEARCH_EXPAND_TARGETING_SWITCH.CLOSE
  }

  return bidParams
}

function makeADABidParams(row) {
  const { bid_strategy, total_budget, totalBudget } = row.bid
  const { ...bidParams } = makeBidParams(row)
  // 总预算不限
  if (!totalBudget) {
    bidParams.total_budget = 0
  } else {
    bidParams.total_budget = Number(total_budget)
  }

  // 出价策略
  bidParams.bid_strategy = bid_strategy

  return bidParams
}

/**
 * @description: 腾讯广告3.0构造计划出价参数
 * @param {*} row
 */
function makeV3BidParams(row) {
  const {
    rangeDate, smart_delivery_goal,
    smart_promotion_spec, game_app_activate_roi_spec, bid_amount_spec, bid_scene, material_package_id,
    auto_acquisition_budget, auto_acquisition_enabled,
    custom_cost_cap, cost_constraint_scene,
    auto_derived_creative_enabled,
    bid_amount_deep_conversion_worth_rate_spec
  } = row.bid
  const { ad_marketing_scene, smart_delivery_platform } = row.plan
  const bidAdjustmentParams = makeBidAdjustmentParams(row.bid, {
    isSmartDelivery: ad_marketing_scene === AD_MARKETING_SCENE.VERSION_SMART,
    taskId: row.taskId
  })

  // 去掉出价策略、总预算
  let { ...bidParams } = makeBidParams(row)

  // 投放日期范围
  const [begin_date, end_date] = rangeDate

  if (auto_acquisition_enabled) {
    // 一键起量预算
    bidParams.auto_acquisition_enabled = auto_acquisition_enabled
    bidParams.auto_acquisition_budget = Number(auto_acquisition_budget)
  } else {
    bidParams.auto_acquisition_enabled = false
  }

  bidParams.begin_date = moment(begin_date).format('YYYY-MM-DD')
  bidParams.end_date = moment(end_date).format('YYYY-MM-DD')

  bidParams.bid_scene = bid_scene
  // 前端辅助字段
  bidParams.ad_marketing_scene = ad_marketing_scene

  const omitKeys = ['bid_amount', 'bid_num_range', 'bid_range', 'auto_acquisition_enabled', 'auto_acquisition_budget']

  // 最大转化量投放删除出价、一键起量、自动出价
  if (bid_scene === BID_SCENE.NORMAL_MAX) {
    bidParams = omit(bidParams, omitKeys)

    const deepSpec = clone(bidParams.deep_conversion_spec)

    // 最大转化去掉深度转化出价，只需要提交goal
    if (deepSpec?.deep_conversion_worth_spec) {
      deepSpec.deep_conversion_worth_spec = pick(deepSpec.deep_conversion_worth_spec, ['goal'])
    }
    if (deepSpec?.deep_conversion_behavior_spec) {
      deepSpec.deep_conversion_behavior_spec = pick(deepSpec.deep_conversion_behavior_spec, ['goal'])
    }

    bidParams.deep_conversion_spec = deepSpec
  }

  if (material_package_id && Number(material_package_id) > 0) {
    // 投放素材标签
    bidParams.material_package_id = Number(material_package_id)
  }

  const { deep_conversion_type } = bidParams.deep_conversion_spec || {}
  if (bid_scene === BID_SCENE.NORMAL_MAX && cost_constraint_scene === COST_CONTROL.OPEN) {
    bidParams.cost_constraint_scene = cost_constraint_scene

    if (deep_conversion_type === DEEP_CONVERSION_TYPE.BEHAVIOR) {
      bidParams.custom_cost_cap = Number(custom_cost_cap)
    }

    if (deep_conversion_type === DEEP_CONVERSION_TYPE.WORTH) {
      bidParams.custom_cost_roi_cap = Number(custom_cost_cap)
    }
  }

  if (auto_derived_creative_enabled) {
    bidParams.auto_derived_creative_enabled = auto_derived_creative_enabled
  }

  // 智投广告参数
  if (ad_marketing_scene === AD_MARKETING_SCENE.VERSION_SMART) {
    //  只有单条修改才有conversion_id_list
    const { conversion_id_list } = row.bid
    const smartPromotionRequiredKeys = ['ad_marketing_scene', 'bid_scene',
      'access_type', 'bid_mode', 'begin_date', 'end_date',
      'time_series', 'daily_budget', 'material_package_id',
      'auto_derived_creative_enabled']
    const { register_cost, first_day_purchase_roi, seven_day_purchase_roi, day30_purchase_roi, iaap_roi, iaap_mix_in_one_factor } = smart_promotion_spec
    const smart_delivery_goal_spec = {}
    const deep_conversion_spec = {
      deep_conversion_type: DEEP_CONVERSION_TYPE.WORTH
    }

    // 智投没有 optimization_goal 的ui显示，按照smart_delivery_platform 和 smart_delivery_goal作对应配置；
    let optimization_goal = ''

    const isSmartDelivMiniGame = smart_delivery_platform === SMART_DELIVERY_PLATFORM.MINI_GAME_PROMOTION
    const isSmartDelivGameApp = smart_delivery_platform === SMART_DELIVERY_PLATFORM.GAME_APP

    // 变现ROI 系列的普通优化目标出价（注册 bid_amount），按 3.0 逻辑传在外层
    let monetizationBidAmount
    // 变现ROI 系列对齐腾讯 3.0：不传 smart_delivery_scene_spec（含 smart_delivery_goal / smart_delivery_goal_spec）
    let isMonetizationSmartGoal = false

    if (isSmartDelivMiniGame) {
      optimization_goal = OPT_GOAL.APP_REGISTER
      switch (smart_delivery_goal) {
        case SMART_DELIVERY_GOAL.FIRST_DAY_PURCHASE_ROI:
          smart_delivery_goal_spec.mini_game_promotion_spec = {
            register_cost: Number(register_cost),
            first_day_purchase_roi: Number(first_day_purchase_roi)
          }
          deep_conversion_spec.deep_conversion_worth_spec = {
            goal: OPT_GOAL.ONE_DAY_PURCHASE_ROAS
          }
          break
        case SMART_DELIVERY_GOAL.SEVEN_DAY_PURCHASE_ROI:
          smart_delivery_goal_spec.mini_game_promotion_spec = {
            register_cost: Number(register_cost),
            seven_day_purchase_roi: Number(seven_day_purchase_roi)
          }
          deep_conversion_spec.deep_conversion_worth_spec = {
            goal: OPT_GOAL.SEVEN_DAY_PURCHASE_ROAS
          }
          break
        case SMART_DELIVERY_GOAL.DAY30_PURCHASE:
          smart_delivery_goal_spec.mini_game_promotion_spec = {
            register_cost: Number(register_cost),
            day30_purchase_roi: Number(day30_purchase_roi)
          }
          deep_conversion_spec.deep_conversion_worth_spec = {
            goal: OPT_GOAL.THIRTY_DAY_PURCHASE_ROAS
          }
          break
        case SMART_DELIVERY_GOAL.IAAP_MIX_IN_ONE:
          smart_delivery_goal_spec.mix_iaap_in_one_spec = {
            iaap_roi: Number(iaap_roi),
            iaap_mix_in_one_factor: Number(iaap_mix_in_one_factor)
          }
          deep_conversion_spec.deep_conversion_worth_spec = {
            goal: OPT_GOAL.ONE_DAY_PURCHASE_MONETIZATION_ROAS
          }
          break
        case SMART_DELIVERY_GOAL.FIRST_DAY_MONETIZATION:
        case SMART_DELIVERY_GOAL.SEVEN_DAY_MONETIZATION:
        case SMART_DELIVERY_GOAL.APP_REGISTER_30DAY_MONETIZATION_ROAS: {
          // 变现ROI：spec 内传 goal + expected_roi（契合后端原参数处理逻辑：expected_roi 提到外层 deep_conversion_worth_rate，再删 deep_conversion_spec）
          // 注册出价 bid_amount 传在外层
          deep_conversion_spec.deep_conversion_worth_spec = {
            goal: smart_delivery_goal,
            expected_roi: Number(smart_promotion_spec.monetization_roi)
          }
          monetizationBidAmount = Number(bid_amount_spec?.bid_amount)
          isMonetizationSmartGoal = true
          break
        }
        case SMART_DELIVERY_GOAL.BACK_FLOW_1DAY_PURCHASE_ROAS:
          smart_delivery_goal_spec.bid_amount_deep_conversion_worth_rate_spec = {
            bid_amount: Number(bid_amount_deep_conversion_worth_rate_spec?.bid_amount),
            deep_conversion_worth_rate: Number(bid_amount_deep_conversion_worth_rate_spec?.deep_conversion_worth_rate)
          }
          deep_conversion_spec.deep_conversion_worth_spec = {
            goal: OPT_GOAL.ONE_DAY_PURCHASE_ROAS
          }
          break
      }
      // 沉默唤起+首日付费ROI 的浅层优化目标为沉默唤起
      if (smart_delivery_goal === SMART_DELIVERY_GOAL.BACK_FLOW_1DAY_PURCHASE_ROAS) {
        optimization_goal = OPT_GOAL.BACK_FLOW
      }
    }
    if (isSmartDelivGameApp) {
      switch (smart_delivery_goal) {
        case SMART_DELIVERY_GOAL.GAME_APP_ACTIVATE_ROI_1DAY:
          {
            const { bid_amount, deep_conversion_worth_rate } = game_app_activate_roi_spec
            smart_delivery_goal_spec.game_app_activate_roi_spec = {
              bid_amount: Number(bid_amount),
              deep_conversion_worth_rate: Number(deep_conversion_worth_rate)
            }

            // app智投激活首日付费roi，deep_conversion_worth_spec必须配置的参数
            // 通过转化id拉取的转化内包含如下格式：
            deep_conversion_spec.deep_conversion_worth_spec = {
              goal: OPT_GOAL.ONE_DAY_PURCHASE_ROAS
            }
          }
          break
        case SMART_DELIVERY_GOAL.ACTIVATE:
        case SMART_DELIVERY_GOAL.APP_PURCHASE:
        case SMART_DELIVERY_GOAL.FIRST_PURCHASE:
          {
            const { bid_amount: activate_bid_amount } = bid_amount_spec
            smart_delivery_goal_spec.bid_amount_spec = {
              bid_amount: Number(activate_bid_amount)
            }
          }

          break
      }

      if ([SMART_DELIVERY_GOAL.GAME_APP_ACTIVATE_ROI_1DAY, SMART_DELIVERY_GOAL.ACTIVATE].includes(smart_delivery_goal)) {
        optimization_goal = OPT_GOAL.APP_ACTIVATE
      }

      if (smart_delivery_goal === SMART_DELIVERY_GOAL.APP_PURCHASE) {
        optimization_goal = OPT_GOAL.APP_PURCHASE
      }
      if (smart_delivery_goal === SMART_DELIVERY_GOAL.FIRST_PURCHASE) {
        optimization_goal = OPT_GOAL.FIRST_PURCHASE
      }
    }

    const smartPromotionParams = {
      ...pick(bidParams, smartPromotionRequiredKeys),
      ...bidAdjustmentParams,
      optimization_goal,
      deep_conversion_spec,
      smart_delivery_platform,
      // 变现ROI 系列对齐腾讯 3.0：不传 smart_delivery_scene_spec
      ...(!isMonetizationSmartGoal
        ? {
          smart_delivery_scene_spec: {
            smart_delivery_goal,
            smart_delivery_goal_spec: bidParams.bid_scene === BID_SCENE.NORMAL_MAX ? undefined : smart_delivery_goal_spec
          }
        }
        : {}),
      // 变现ROI 系列：普通优化目标出价（注册 bid_amount）按 3.0 传在外层
      ...(monetizationBidAmount !== undefined && !isNaN(monetizationBidAmount) ? { bid_amount: monetizationBidAmount } : {})
    }

    //  为智投新增出价场景参数，最大转化量投放必须传入日预算
    if (bidParams.bid_scene === BID_SCENE.NORMAL_MAX) {
      smartPromotionParams.daily_budget = bidParams.daily_budget
    }

    // 为单条修改加上转化id
    if (conversion_id_list) {
      smartPromotionParams.smart_delivery_scene_spec.conversion_id_list = conversion_id_list
    }

    return smartPromotionParams
  }

  return bidParams
}

/**
   * @description: 获取腾讯广告搜索广告提交参数
   * @return {*}
   */
export function makeSearchAdParams(originParams, params) {
  const { inner_plan_params, outer_plan_params, creative_params } = params
  const { ch_platform_id } = inner_plan_params
  const {
    access_type, begin_date, end_date, time_series,
    bid_amount, bid_range, bid_num_range, daily_budget: ad_daily_budget,
    bid_scene, scene_spec, optimization_goal, deep_conversion_spec,
    targeting, site_set, marketing_carrier_detail,
    operation, ad_marketing_scene
  } = outer_plan_params

  const { target, ad_account_id } = originParams
  const { budget, daily_budget } = originParams.plan
  const { description_component_options, dynamic_creative_name } = originParams.creative
  const { brand, description, image_list, image, video, main_jump_info } = creative_params.creative_components
  const promoted_object_type = ['', 'PROMOTED_OBJECT_TYPE_APP_ANDROID', 'PROMOTED_OBJECT_TYPE_APP_IOS', 'PROMOTED_OBJECT_TYPE_MINI_GAME_WECHAT'][ch_platform_id]
  const promoted_object_id = marketing_carrier_detail.marketing_carrier_id
  const pageInfoValue = {
    page_type: '',
    page_spec: {},
    media_query: { media_type: 'ALL' }
  }
  const { official_spec } = main_jump_info[0].value?.page_spec || {}

  switch (ch_platform_id) {
    case PLATFORM.ANDROID:
      if (official_spec) {
        pageInfoValue.page_spec = official_spec
      }
      pageInfoValue.page_type = 'PAGE_TYPE_LINGQUE_H5'
      break

    case PLATFORM.IOS:
      pageInfoValue.page_type = 'PAGE_TYPE_DEFAULT'

      if (official_spec) {
        pageInfoValue.page_spec = official_spec
        pageInfoValue.page_type = 'PAGE_TYPE_LINGQUE_H5'
      }
      break

    case PLATFORM.MINIGAME:
      pageInfoValue.page_type = 'PAGE_TYPE_MINI_GAME_WECHAT'
      break

    default:
      break
  }

  const { brand_name, brand_image_id } = brand[0].value
  const campaign_type = 'CAMPAIGN_TYPE_SEARCH'

  const image_component_options = []
  const image3_component_options = []

  if (image?.length) {
    for (const img of image) {
      const { image_id } = img.value

      // ratio的计算在makeV3CreativeParams方法内
      const { ratio } = creative_params.materials.find(i => i.key === image_id) || {}

      // 16:9的尺寸图片放到image3_component_options
      if (ratio > 1 && ratio.toFixed(10) === '1.7777777778') {
        image3_component_options.push(img)
      } else {
        // 其他的放到image_component_options
        image_component_options.push(img)
      }
    }
  }

  return {
    ad_account_id,
    campaign_params: {
      operation,
      campaign_type,
      promoted_object_type,
      daily_budget: budget === 1 ? daily_budget : 0
    },
    inner_plan_params,
    outer_plan_params: {
      operation,
      ad_marketing_scene,
      access_type,
      bid_scene,
      time_series,
      begin_date,
      end_date,
      bid_mode: BUDGET_CONSTS.BID_MODE.OCPC,
      bid_amount,
      bid_range,
      bid_num_range,
      daily_budget: ad_daily_budget,
      optimization_goal,
      deep_conversion_spec,
      scene_spec,
      targeting,
      automatic_site_enabled: false,
      site_set,
      promoted_object_type,
      promoted_object_id,
      searchad_bidword_list: Array.isArray(target.searchAdBidKeywords) ? target.searchAdBidKeywords.map(w => ({ bidword: w.keyword, match_type: w.patern.value })) : []
    },
    creative_params: {
      campaign_type,
      promoted_object_type,
      promoted_object_id,
      dynamic_creative_template_id: 2083,
      dynamic_creative_name: dynamic_creative_name,
      automatic_site_enabled: false,
      dynamic_creative_type: 'DYNAMIC_CREATIVE_TYPE_COMPONENT',
      site_set,
      materials: creative_params.materials,
      dynamic_creative_elements: {
        description_component_options: description_component_options,
        brand_component_options: [{
          value: {
            brand_name,
            brand_img: {
              image_id: brand_image_id
            }
          }
        }],
        title_component_options: description.map(desc => ({ value: desc.value.content })),
        image_list_component_options: image_list?.map(i => ({ value: i.value.list })),
        image_component_options: image_component_options?.length ? image_component_options : undefined,
        image3_component_options: image3_component_options?.length ? image3_component_options : undefined,
        video_component_options: video?.map(v => {
          const { cover_id, video_id } = v.value
          return {
            value: {
              video: {
                video_id
              },
              cover_image: {
                image_id: cover_id
              }
            }
          }
        }),
        landing_page_component_options: [{ value: [pageInfoValue] }]
      }
    }
  }
}
